"use client";
import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { FONT_MONO } from "theme/fonts";
// ── Constants ─────────────────────────────────────────────────────────────
const SAMPLING_RATE = 75;
const DEFAULT_TIME_BASE = 5;
const MIN_TIME_BASE = 1;
const MAX_TIME_BASE = 10;
const Y_AXIS_W = 50;
const X_AXIS_H = 18;

const MIN_SCROLL_SPEED = 0.3;
const MAX_SCROLL_SPEED = 6;

const SMOOTH_ALPHA = 0.08;
const BASELINE_ALPHA = 0.0005;
const ADC_RANGE_10BIT = 512;
const ADC_RANGE_12BIT = 2048;

// ── REM display constants (mirrors home.tsx) ──────────────────────────────
const REM_DISPLAY_WINDOW_MS  = 10_000;
const REM_DISPLAY_THRESHOLD  = 200;

// ── Recording snapshot interval (5 minutes) ──────────────────────────────
const SNAPSHOT_INTERVAL_MS = 5 * 60 * 1000;

// ── Types ──────────────────────────────────────────────────────────────────
interface EyeBand { label: string; min: number; max: number; color: string; desc: string; }
interface BandAmpLine { label: string; color: string; ampUV: number; dash: number[]; }
interface Channel { id: number; label: string; color: string; }
interface EogMode { id: number | null; label: string; icon: string; desc: string; }
interface NotchMode { id: number | null; label: string; }
interface AmplitudeMap { ch1?: number; ch2?: number; ch3?: number; [key: string]: number | undefined; }
interface HistoryEntry { ch1?: number; hz?: number; time?: number; [key: string]: number | undefined; }
interface RecordingEntry { ch1?: number; ch2?: number; ch3?: number; [key: string]: number | undefined; }
interface FilterMap { [channelId: number]: number | null; }
interface DirectionInfo { label: string; color: string; }
interface DrawBandLinesOptions { midY: number; scale: number; xStart: number; xEnd: number; plotTop: number; plotH: number; showLabels?: boolean; }

// ── Session Snapshot (every 5 mins during recording) ─────────────────────
interface SessionSnapshot {
  timestampMs: number;       // wall-clock time
  elapsedMs: number;         // ms since recording started
  ch1AC: number;             // AC-coupled ch1 value at snapshot time
  ch1Raw: number;            // raw ch1
  ch1Baseline: number;       // estimated DC baseline
  eyeState: string;          // eye state at that moment
  bandLabel: string;         // dominant band (Delta/Theta/Alpha/Beta/Gamma/–)
  bandColor: string;
  acHistory: number[];       // last 60 AC samples for mini sparkline (~0.8s of data)
}

// ── Ring buffer — O(1) writes, no array shifting ──────────────────────────
class RingBuffer {
  private buf: Float32Array;
  private head: number = 0;
  public length: number;
  public filled: number = 0;

  constructor(size: number, initial?: Float32Array) {
    this.length = size;
    this.buf = new Float32Array(size);
    if (initial) {
      const n = Math.min(initial.length, size);
      this.buf.set(initial.subarray(0, n));
      this.filled = n;
      this.head = n % size;
    }
  }

  push(val: number): void {
    this.buf[this.head] = val;
    this.head = (this.head + 1) % this.length;
    if (this.filled < this.length) this.filled++;
  }

  readInto(out: Float32Array): void {
    const n = Math.min(this.filled, out.length);
    for (let i = 0; i < n; i++) {
      const idx = (this.head - 1 - i + this.length) % this.length;
      out[i] = this.buf[idx];
    }
  }

  toFloat32(): Float32Array {
    return this.buf.slice();
  }

  resize(newSize: number): RingBuffer {
    const tmp = new Float32Array(Math.min(this.filled, newSize));
    this.readInto(tmp);
    tmp.reverse();
    return new RingBuffer(newSize, tmp);
  }

  clear(): void {
    this.buf.fill(0);
    this.head = 0;
    this.filled = 0;
  }
}

interface CanvasEntry {
  canvas: HTMLCanvasElement;
  ch: Channel;
  isLastCh: boolean;
  idx: number;
  plotW: number;
}

interface ScrollingCanvasState {
  rings: RingBuffer[];
  readBuf: Float32Array[];
  baselineVals: (number | null)[];
  smoothedVals: (number | null)[];
  plotW: number;
  rafId: number | null;
  canvases: CanvasEntry[];
  fracAccum: number;
  detectedRange: number;
  detectedRangeLocked: boolean;
  sampleCount: number;
  dirty: boolean;
}

interface UseScrollingCanvasOptions {
  containerRef: React.RefObject<HTMLDivElement | null>;
  selectedChannels: number[];
  amplRef: React.RefObject<AmplitudeMap>;
  connected: boolean;
  paused: boolean;
  timeBase: number;
  zoom: number;
  scrollSpeed: number;
}

const EYE_BANDS: EyeBand[] = [
  { label: "Delta", min: 0.5, max: 4,   color: "#a78bfa", desc: "Deep slow waves" },
  { label: "Theta", min: 4,   max: 8,   color: "#38bdf8", desc: "Drowsy / microsleep" },
  { label: "Alpha", min: 8,   max: 13,  color: "#34d399", desc: "Relaxed / closed eyes" },
  { label: "Beta",  min: 13,  max: 30,  color: "#facc15", desc: "Active focus" },
  { label: "Gamma", min: 30,  max: 100, color: "#f87171", desc: "High cognitive load" },
];

const BAND_AMP_LINES: BandAmpLine[] = [
  { label: "δ Delta", color: "#f472b6", ampUV: 40,  dash: [6, 4] },  // pink
  { label: "θ Theta", color: "#fb923c", ampUV: 80,  dash: [4, 4] },  // orange
  { label: "α Alpha", color: "#e879f9", ampUV: 130, dash: [8, 3] },  // fuchsia
  { label: "β Beta",  color: "#a3e635", ampUV: 200, dash: [3, 3] },  // lime
  { label: "γ Gamma", color: "#f43f5e", ampUV: 310, dash: [2, 4] },  // rose-red
];
const CHANNELS: Channel[] = [
  { id: 1, label: "CH1", color: "#38bdf8" },
  { id: 2, label: "CH2", color: "#a78bfa" },
  { id: 3, label: "CH3", color: "#34d399" },
];

const DEFAULT_SELECTED_CHANNELS: number[] = [1];

const EOG_MODES: EogMode[] = [
  { id: null, label: "Off", icon: "✕", desc: "No filter" },
  { id: 2,    label: "EOG", icon: "👁", desc: "Eye movement (0.5–19.5 Hz)" },
];

const NOTCH_MODES: NotchMode[] = [
  { id: null, label: "Off" },
  { id: 1,    label: "50 Hz" },
  { id: 2,    label: "60 Hz" },
];

function deriveScrollSpeed(timeBase: number, plotW: number): number {
  const pxPerSec = plotW / timeBase;
  const pxPerFrame = pxPerSec / 60;
  return Math.max(MIN_SCROLL_SPEED, Math.min(MAX_SCROLL_SPEED, pxPerFrame));
}

function fmtElapsed(s: number): string {
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

function fmtTime(ms: number): string {
  const d = new Date(ms);
  return [d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds()]
    .map((v) => String(v).padStart(2, "0")).join(":");
}

function fmtDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function getActiveBand(hz: number): EyeBand | null {
  return EYE_BANDS.find((b) => hz >= b.min && hz < b.max) ?? null;
}

// ── Derive dominant band from AC amplitude magnitude ─────────────────────
// Maps absolute AC deflection to the most likely band (rough heuristic for display)
function getBandFromAC(acAbs: number): { label: string; color: string } {
  if (acAbs < 40)  return { label: "δ Delta", color: "#f472b6" };
  if (acAbs < 80)  return { label: "θ Theta", color: "#fb923c" };
  if (acAbs < 130) return { label: "α Alpha", color: "#e879f9" };
  if (acAbs < 200) return { label: "β Beta",  color: "#a3e635" };
  return              { label: "γ Gamma", color: "#f43f5e" };
}

function classifyDir(acVal: number, range: number): DirectionInfo {
  const threshold1 = range * 0.08;
  const threshold2 = range * 0.03;
  if (acVal > threshold1)  return { label: "RIGHT →", color: "#38bdf8" };
  if (acVal < -threshold1) return { label: "← LEFT",  color: "#a78bfa" };
  if (acVal > threshold2)  return { label: "right →",  color: "#38bdf877" };
  if (acVal < -threshold2) return { label: "← left",   color: "#a78bfa77" };
  return { label: "CENTER ●", color: "#64748b" };
}

function battColor(lvl: number | null): string {
  if (lvl === null) return "#475569";
  if (lvl > 60) return "#34d399";
  if (lvl > 30) return "#facc15";
  if (lvl > 15) return "#fb923c";
  return "#f87171";
}

function battSegs(lvl: number | null): number {
  return lvl === null ? 0 : Math.ceil(lvl / 25);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function detectAdcRange(rawVal: number): number {
  return rawVal > 1500 ? ADC_RANGE_12BIT : ADC_RANGE_10BIT;
}

function drawBandLines(ctx: CanvasRenderingContext2D, { midY, scale, xStart, xEnd, plotTop, plotH, showLabels = true }: DrawBandLinesOptions): void {
  BAND_AMP_LINES.forEach(({ label, color, ampUV, dash }) => {
    ([-1, 1] as const).forEach((sign) => {
      const y = midY - sign * ampUV * scale;
      if (y < plotTop || y > plotTop + plotH) return;
      ctx.save();
      ctx.strokeStyle = color; ctx.lineWidth = 0.8; ctx.globalAlpha = 0.45; ctx.setLineDash(dash);
      ctx.beginPath(); ctx.moveTo(xStart, y); ctx.lineTo(xEnd, y); ctx.stroke();
      ctx.setLineDash([]); ctx.globalAlpha = 1;
      if (showLabels && sign === 1) {
        ctx.globalAlpha = 0.75; ctx.fillStyle = color; ctx.font = "7px 'JetBrains Mono',monospace";
        ctx.textAlign = "right"; ctx.textBaseline = "bottom";
        ctx.fillText(label, xEnd - 2, y - 1); ctx.globalAlpha = 1;
      }
      ctx.restore();
    });
  });
}

function computeRemScore(eogHistory: HistoryEntry[], threshold: number): number {
  if (eogHistory.length < 5) return 0;
  const now    = Date.now();
  const cutoff = now - REM_DISPLAY_WINDOW_MS;
  const window = eogHistory.filter(e => (e.time ?? 0) >= cutoff);
  if (window.length < 5) return 0;
  let total = 0, count = 0;
  for (let i = 1; i < window.length; i++) {
    total += Math.abs((window[i].ch1 ?? 0) - (window[i - 1].ch1 ?? 0));
    count++;
  }
  const avg = count > 0 ? total / count : 0;
  return Math.min(1, avg / Math.max(1, threshold));
}

interface BatteryShellProps { segs: number; color: string; lvl: number | null; }
function BatteryShell({ segs, color, lvl }: BatteryShellProps) {
  const W = 22, H = 12, NW = 3, NH = 5, pad = 2, gap = 1.5;
  const cw = (W - pad * 2 - gap * 3) / 4;
  return (
    <svg width={W + NW} height={H} viewBox={`0 0 ${W + NW} ${H}`} style={{ flexShrink: 0 }}>
      <rect x={0.5} y={0.5} width={W - 1} height={H - 1} rx={2.5} fill="none" stroke={color} strokeWidth={1.2} opacity={0.6} />
      <rect x={W} y={(H - NH) / 2} width={NW - 0.5} height={NH} rx={1} fill={color} opacity={0.5} />
      {[0, 1, 2, 3].map((i) => (
        <rect key={i} x={pad + i * (cw + gap)} y={pad} width={cw} height={H - pad * 2} rx={1}
          fill={i < segs ? color : "transparent"} opacity={i < segs ? (lvl !== null && lvl <= 15 ? 0.9 : 0.75) : 0}
          style={i < segs && lvl !== null && lvl <= 15 ? { animation: "battCellPulse 1.2s ease-in-out infinite" } : {}} />
      ))}
    </svg>
  );
}

interface BatteryIndicatorProps { level: number | null; connected: boolean; }
function BatteryIndicator({ level, connected }: BatteryIndicatorProps) {
  const c = battColor(level), s = battSegs(level), low = level !== null && level <= 15;
  if (!connected)
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 8px", background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, opacity: 0.4 }}>
        <BatteryShell segs={0} color="#334155" lvl={null} />
        <span style={{ fontSize: 10, color: "#334155", fontWeight: 600 }}>—</span>
      </div>
    );
  return (
    <div title={level !== null ? `Battery: ${level}%` : "Unknown"}
      style={{ display: "flex", alignItems: "center", gap: 7, padding: "3px 9px", background: low ? "#1e0b0b" : "#0f172a", border: `1px solid ${c}${low ? "88" : "33"}`, borderRadius: 8, transition: "all .4s", animation: low ? "battPulse 1.2s ease-in-out infinite" : "none" }}>
      <BatteryShell segs={s} color={c} lvl={level} />
      <div style={{ display: "flex", flexDirection: "column" }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: c, lineHeight: 1, fontVariantNumeric: "tabular-nums", letterSpacing: "-.5px" }}>
          {level !== null ? `${level}%` : "—"}
        </span>
        <span style={{ fontSize: 8, color: c + "99", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, lineHeight: 1.2 }}>
          {level === null ? "unknown" : low ? "⚠ LOW" : level <= 30 ? "low" : "batt"}
        </span>
      </div>
    </div>
  );
}

interface EogBtnGroupProps { active: number | null | "__mixed__"; onSelect: (v: number | null) => void; }
function EogBtnGroup({ active, onSelect }: EogBtnGroupProps) {
  return (
    <div style={{ display: "flex", border: "1px solid #1e293b", borderRadius: 8, overflow: "hidden" }}>
      {EOG_MODES.map((m, i) => {
        const isActive = active === m.id;
        const col = m.id === null ? "#f87171" : "#34d399";
        return (
          <button key={m.id ?? "off-eog"} title={m.desc} onClick={() => onSelect(m.id)}
            style={{ padding: "4px 10px", border: "none", outline: "none", background: isActive ? col + "33" : "transparent", color: isActive ? col : "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer", borderRight: i < EOG_MODES.length - 1 ? "1px solid #1e293b" : "none", transition: "all .15s", display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ fontSize: 13 }}>{m.icon}</span>
            <span style={{ fontSize: 10 }}>{m.label}</span>
          </button>
        );
      })}
    </div>
  );
}

interface NotchBtnGroupProps { active: number | null | "__mixed__"; onSelect: (v: number | null) => void; }
function NotchBtnGroup({ active, onSelect }: NotchBtnGroupProps) {
  return (
    <div style={{ display: "flex", border: "1px solid #1e293b", borderRadius: 8, overflow: "hidden" }}>
      {NOTCH_MODES.map((m, i) => {
        const isActive = active === m.id;
        const col = m.id === null ? "#f87171" : "#facc15";
        return (
          <button key={m.id ?? "notch-off"} onClick={() => onSelect(m.id)}
            style={{ padding: "4px 9px", border: "none", outline: "none", background: isActive ? col + "33" : "transparent", color: isActive ? col : "#64748b", fontSize: 11, fontWeight: 600, cursor: "pointer", borderRight: i < NOTCH_MODES.length - 1 ? "1px solid #1e293b" : "none", transition: "all .15s" }}>
            {m.label}
          </button>
        );
      })}
    </div>
  );
}

interface LabelSliderProps { label: string; value: number; min: number; max: number; step?: number; onChange: (v: number) => void; fmtVal?: (v: number) => string; color?: string; }
function LabelSlider({ label, value, min, max, step = 1, onChange, fmtVal, color = "#38bdf8" }: LabelSliderProps) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div style={{ flex: "1 1 160px" }}>
      <div style={{ fontSize: 10, color: "#475569", marginBottom: 4 }}>
        <span style={{ color: "#94a3b8", fontWeight: 700 }}>{label}:</span>{" "}
        <span style={{ color }}>{fmtVal ? fmtVal(value) : value}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#0a1525", border: "1px solid #1e293b", borderRadius: 8, padding: "4px 8px" }}>
        <button onClick={() => onChange(min)} style={{ fontSize: 9, color: "#475569", background: "#1e293b", border: "none", borderRadius: 4, padding: "2px 5px", cursor: "pointer" }}>{min}</button>
        <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="hz-slider"
          style={{ flex: 1, height: 3, borderRadius: 4, appearance: "none", outline: "none", cursor: "pointer", background: `linear-gradient(to right,${color} ${pct}%,#1e293b ${pct}%)` }} />
        <button onClick={() => onChange(max)} style={{ fontSize: 9, color: "#475569", background: "#1e293b", border: "none", borderRadius: 4, padding: "2px 5px", cursor: "pointer" }}>{max}</button>
      </div>
    </div>
  );
}

function BandLegendRow() {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "6px 10px", background: "#07101e", border: "1px solid #1e293b", borderRadius: 8, marginBottom: 10 }}>
      <span style={{ fontSize: 9, color: "#475569", fontWeight: 700, alignSelf: "center", marginRight: 4, letterSpacing: 1, textTransform: "uppercase" }}>Band levels:</span>
      {BAND_AMP_LINES.map((b) => (
        <div key={b.label} style={{ display: "flex", alignItems: "center", gap: 4, background: b.color + "12", border: `1px solid ${b.color}44`, borderRadius: 5, padding: "2px 8px" }}>
          <svg width="18" height="8" style={{ flexShrink: 0 }}>
            <line x1="0" y1="4" x2="18" y2="4" stroke={b.color} strokeWidth="1.5" strokeDasharray={b.dash.join(",")} opacity="0.9" />
          </svg>
          <span style={{ fontSize: 9, fontWeight: 700, color: b.color }}>{b.label}</span>
          <span style={{ fontSize: 8, color: "#475569" }}>±{b.ampUV}</span>
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// ── SESSION REPORT MODAL ──────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════

interface SessionReportModalProps {
  snapshots: SessionSnapshot[];
  totalDurationMs: number;
  startedAt: number; // wall clock ms
  onClose: () => void;
}

// Mini sparkline canvas for each snapshot row
function MiniSparkline({ data, color, height = 32, width = 120 }: { data: number[]; color: string; height?: number; width?: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || data.length < 2) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    const max = Math.max(...data.map(Math.abs), 1);
    const midY = H / 2;
    const xScale = W / (data.length - 1);

    // Zero line
    ctx.strokeStyle = "#1e3a5f";
    ctx.lineWidth = 0.5;
    ctx.setLineDash([2, 3]);
    ctx.beginPath();
    ctx.moveTo(0, midY);
    ctx.lineTo(W, midY);
    ctx.stroke();
    ctx.setLineDash([]);

    // Signal
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.2;
    ctx.globalAlpha = 0.9;
    ctx.beginPath();
    data.forEach((v, i) => {
      const x = i * xScale;
      const y = midY - (v / max) * (H / 2 - 2);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();
    ctx.globalAlpha = 1;
  }, [data, color]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{ display: "block", borderRadius: 4, background: "#060d1a" }}
    />
  );
}

// State icon/emoji map
function eyeStateEmoji(state: string): string {
  if (state === "dreaming") return "💤";
  if (state === "closed")   return "🌑";
  if (state === "half")     return "😴";
  return "👁";
}

function eyeStateColor(state: string): string {
  if (state === "dreaming") return "#c8a050";
  if (state === "closed")   return "#34d399";
  if (state === "half")     return "#9b72dd";
  return "#4fc3f7";
}

function eyeStateLabel(state: string): string {
  if (state === "dreaming") return "Dreaming";
  if (state === "closed")   return "Deep Sleep";
  if (state === "half")     return "Drowsy";
  return "Awake";
}

// ── Draw a large combined AC timeline across all snapshots ────────────────
function FullTimelineCanvas({ snapshots, height = 90 }: { snapshots: SessionSnapshot[]; height?: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || snapshots.length === 0) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    // Flatten all AC history across snapshots in order
    const allPoints: { val: number; snapshotIdx: number }[] = [];
    snapshots.forEach((snap, si) => {
      snap.acHistory.forEach(v => allPoints.push({ val: v, snapshotIdx: si }));
    });

    if (allPoints.length < 2) return;

    const max = Math.max(...allPoints.map(p => Math.abs(p.val)), 1);
    const midY = H / 2;
    const xScale = W / (allPoints.length - 1);

    // Grid
    ctx.strokeStyle = "#0e1e30";
    ctx.lineWidth = 0.5;
    [0.25, 0.5, 0.75].forEach(f => {
      ctx.beginPath();
      ctx.moveTo(0, H * f);
      ctx.lineTo(W, H * f);
      ctx.stroke();
    });

    // Snapshot dividers
    let runningIdx = 0;
    snapshots.forEach((snap, si) => {
      if (si === 0) { runningIdx += snap.acHistory.length; return; }
      const x = runningIdx * xScale;
      ctx.strokeStyle = "#1e3a5f";
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, H);
      ctx.stroke();
      ctx.setLineDash([]);
      runningIdx += snap.acHistory.length;
    });

    // Signal path with color per band
    allPoints.forEach((p, i) => {
      if (i === 0) return;
      const snap = snapshots[p.snapshotIdx];
      const x0 = (i - 1) * xScale;
      const x1 = i * xScale;
      const y0 = midY - (allPoints[i - 1].val / max) * (midY - 2);
      const y1 = midY - (p.val / max) * (midY - 2);
      ctx.strokeStyle = snap.bandColor;
      ctx.lineWidth = 1.2;
      ctx.globalAlpha = 0.8;
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.stroke();
    });
    ctx.globalAlpha = 1;

  }, [snapshots]);

  return (
    <canvas
      ref={canvasRef}
      width={700}
      height={height}
      style={{ display: "block", width: "100%", height, borderRadius: 6, background: "#07101e" }}
    />
  );
}
function SessionReportModal({ snapshots, totalDurationMs, startedAt, onClose }: SessionReportModalProps) {
  // Summary stats
  
  const stateCount: Record<string, number> = {};
  snapshots.forEach(s => { stateCount[s.eyeState] = (stateCount[s.eyeState] ?? 0) + 1; });
  const dominantState = Object.entries(stateCount).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "open";

  const bandCount: Record<string, number> = {};
  snapshots.forEach(s => { bandCount[s.bandLabel] = (bandCount[s.bandLabel] ?? 0) + 1; });
  const dominantBand = Object.entries(bandCount).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "–";
  const dominantBandColor = snapshots.find(s => s.bandLabel === dominantBand)?.bandColor ?? "#64748b";

  const avgAC = snapshots.length > 0
    ? snapshots.reduce((s, x) => s + Math.abs(x.ch1AC), 0) / snapshots.length
    : 0;

  const dreamSnapshots = snapshots.filter(s => s.eyeState === "dreaming").length;
  const dreamPct = snapshots.length > 0 ? Math.round((dreamSnapshots / snapshots.length) * 100) : 0;

  const startedStr = new Date(startedAt).toLocaleTimeString();

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(3,7,18,0.94)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
        backdropFilter: "blur(4px)",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: "#0b1628",
        border: "1px solid #1e3a5f",
        borderRadius: 18,
        width: "min(960px, 96vw)",
        maxHeight: "92vh",
        overflowY: "auto",
        padding: 24,
        scrollbarWidth: "thin",
        scrollbarColor: "#1e3a5f transparent",
        fontFamily: FONT_MONO,
      }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#e2e8f0", marginBottom: 4 }}>
              🌙 Session Recording Report
            </div>
            <div style={{ fontSize: 10, color: "#475569" }}>
              Started: {startedStr} · Duration: {fmtDuration(totalDurationMs)} · {snapshots.length} snapshots (every 5 min)
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 8, color: "#94a3b8", padding: "6px 14px", cursor: "pointer", fontSize: 12, flexShrink: 0 }}
          >
            ✕
          </button>
        </div>

        {snapshots.length === 0 ? (
          <div style={{ textAlign: "center", color: "#334155", padding: "40px 0", fontSize: 13 }}>
            No snapshots captured yet — recording was too short (less than 5 minutes).
          </div>
        ) : (
          <>
            {/* ── Summary cards ── */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
              {[
                { label: "Total Duration",   value: fmtDuration(totalDurationMs), color: "#38bdf8", icon: "⏱" },
                { label: "Dominant State",   value: eyeStateLabel(dominantState),  color: eyeStateColor(dominantState), icon: eyeStateEmoji(dominantState) },
                { label: "Dominant Band",    value: dominantBand,                  color: dominantBandColor, icon: "🌊" },
                { label: "Avg AC Amplitude", value: `±${avgAC.toFixed(0)}`,        color: "#34d399", icon: "📈" },
                { label: "Dreaming %",       value: `${dreamPct}%`,                color: "#c8a050", icon: "💤" },
                { label: "Snapshots",        value: String(snapshots.length),      color: "#a78bfa", icon: "📸" },
              ].map(({ label, value, color, icon }) => (
                <div key={label} style={{
                  flex: "1 1 120px",
                  background: "#07101e",
                  border: `1px solid ${color}22`,
                  borderRadius: 10,
                  padding: "10px 12px",
                }}>
                  <div style={{ fontSize: 11, marginBottom: 3 }}>{icon}</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color }}>{value}</div>
                  <div style={{ fontSize: 9, color: "#334155", letterSpacing: 1, textTransform: "uppercase" }}>{label}</div>
                </div>
              ))}
            </div>

            {/* ── Full session timeline ── */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 9, color: "#475569", letterSpacing: 2, textTransform: "uppercase", fontWeight: 700, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ color: "#38bdf8" }}>📊</span> Full Session AC Waveform
                <span style={{ fontSize: 8, color: "#1e3a5f", background: "#07101e", border: "1px solid #1e3a5f", borderRadius: 4, padding: "1px 6px" }}>
                  coloured by dominant band · dividers = 5-min marks
                </span>
              </div>
              <FullTimelineCanvas snapshots={snapshots} height={90} />

              {/* Band color legend under timeline */}
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 6 }}>
                {BAND_AMP_LINES.map(b => (
                  <div key={b.label} style={{ display: "flex", alignItems: "center", gap: 4, background: b.color + "11", border: `1px solid ${b.color}33`, borderRadius: 4, padding: "2px 6px" }}>
                    <svg width="12" height="6"><line x1="0" y1="3" x2="12" y2="3" stroke={b.color} strokeWidth="1.5" strokeDasharray={b.dash.join(",")} /></svg>
                    <span style={{ fontSize: 8, color: b.color }}>{b.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Per-snapshot rows ── */}
            <div style={{ fontSize: 9, color: "#475569", letterSpacing: 2, textTransform: "uppercase", fontWeight: 700, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ color: "#a78bfa" }}>📸</span> 5-Minute Snapshots
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {snapshots.map((snap, idx) => {
                const stateColor = eyeStateColor(snap.eyeState);
                const elapsedLabel = fmtDuration(snap.elapsedMs);
                const timeLabel = new Date(snap.timestampMs).toLocaleTimeString();

                return (
                  <div key={idx} style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    background: snap.eyeState === "dreaming" ? "#1a100d" : "#07101e",
                    border: `1px solid ${snap.eyeState === "dreaming" ? "#c8a05033" : "#1e293b"}`,
                    borderRadius: 10,
                    padding: "10px 12px",
                    flexWrap: "wrap",
                    transition: "background 0.3s",
                  }}>
                    {/* Index + time */}
                    <div style={{ minWidth: 44, flexShrink: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: "#38bdf8", lineHeight: 1 }}>
                        #{idx + 1}
                      </div>
                      <div style={{ fontSize: 8, color: "#334155" }}>{elapsedLabel}</div>
                      <div style={{ fontSize: 7, color: "#1e3a5f" }}>{timeLabel}</div>
                    </div>

                    {/* State badge */}
                    <div style={{
                      display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                      background: stateColor + "22",
                      border: `1px solid ${stateColor}44`,
                      borderRadius: 8, padding: "5px 9px",
                      minWidth: 70, flexShrink: 0,
                    }}>
                      <span style={{ fontSize: 16 }}>{eyeStateEmoji(snap.eyeState)}</span>
                      <span style={{ fontSize: 8, color: stateColor, fontWeight: 700, letterSpacing: 0.5 }}>
                        {eyeStateLabel(snap.eyeState)}
                      </span>
                    </div>

                    {/* Band badge */}
                    <div style={{
                      display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                      background: snap.bandColor + "18",
                      border: `1px solid ${snap.bandColor}44`,
                      borderRadius: 8, padding: "5px 9px",
                      minWidth: 68, flexShrink: 0,
                    }}>
                      <span style={{ fontSize: 9, color: snap.bandColor, fontWeight: 800 }}>{snap.bandLabel}</span>
                      <span style={{ fontSize: 7, color: "#334155" }}>band</span>
                    </div>

                    {/* AC value */}
                    <div style={{
                      display: "flex", flexDirection: "column", gap: 1, minWidth: 64, flexShrink: 0,
                    }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: snap.eyeState === "dreaming" ? "#c8a050" : "#38bdf8" }}>
                        {(snap.ch1AC > 0 ? "+" : "") + snap.ch1AC.toFixed(0)}
                        <span style={{ fontSize: 8, color: "#475569", marginLeft: 2 }}>Δac</span>
                      </div>
                      <div style={{ fontSize: 8, color: "#334155" }}>raw: {snap.ch1Raw.toFixed(0)}</div>
                      <div style={{ fontSize: 8, color: "#1e3a5f" }}>dc≈{snap.ch1Baseline.toFixed(0)}</div>

                      {/* Mini AC bar */}
                      <div style={{ height: 3, background: "#1e293b", borderRadius: 3, overflow: "hidden", width: 60, marginTop: 3 }}>
                        <div style={{
                          height: "100%",
                          width: `${Math.min(Math.abs(snap.ch1AC) / 300 * 100, 100)}%`,
                          background: snap.bandColor,
                          borderRadius: 3,
                        }} />
                      </div>
                    </div>

                    {/* Sparkline */}
                    <div style={{ flex: 1, minWidth: 80, maxWidth: 160 }}>
                      <div style={{ fontSize: 7, color: "#334155", marginBottom: 3, letterSpacing: 1 }}>
                        CH1 AC · last {snap.acHistory.length} samples
                      </div>
                      <MiniSparkline data={snap.acHistory} color={snap.bandColor} height={32} width={140} />
                    </div>

                    {/* Band amplitude reference lines indicator */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 2, flexShrink: 0 }}>
                      {BAND_AMP_LINES.map(b => {
                        const isActive = b.label === snap.bandLabel;
                        return (
                          <div key={b.label} style={{ display: "flex", alignItems: "center", gap: 3 }}>
                            <div style={{
                              width: 20, height: 2,
                              background: b.color,
                              opacity: isActive ? 1 : 0.18,
                              borderRadius: 2,
                            }} />
                            <span style={{ fontSize: 7, color: isActive ? b.color : "#1e293b", fontWeight: isActive ? 800 : 400 }}>
                              {b.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── State distribution bar ── */}
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 9, color: "#475569", letterSpacing: 2, textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>
                Eye State Distribution
              </div>
              <div style={{ display: "flex", gap: 3, height: 12, borderRadius: 6, overflow: "hidden" }}>
                {(["open", "half", "closed", "dreaming"] as const).map(st => {
                  const cnt = stateCount[st] ?? 0;
                  const pct = snapshots.length > 0 ? (cnt / snapshots.length) * 100 : 0;
                  if (pct === 0) return null;
                  return (
                    <div key={st} title={`${eyeStateLabel(st)}: ${cnt} snapshots (${pct.toFixed(0)}%)`}
                      style={{ flex: pct, background: eyeStateColor(st), opacity: 0.8 }} />
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 6, flexWrap: "wrap" }}>
                {(["open", "half", "closed", "dreaming"] as const).map(st => {
                  const cnt = stateCount[st] ?? 0;
                  const pct = snapshots.length > 0 ? (cnt / snapshots.length) * 100 : 0;
                  return (
                    <div key={st} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: eyeStateColor(st), opacity: cnt > 0 ? 0.8 : 0.2 }} />
                      <span style={{ fontSize: 8, color: cnt > 0 ? eyeStateColor(st) : "#1e293b" }}>
                        {eyeStateLabel(st)}: {pct.toFixed(0)}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

          </>
        )}
      </div>
    </div>
  );
}

// ── AnalysisModal (original, kept intact) ─────────────────────────────────
interface AnalysisModalProps { recordingData: RecordingEntry[]; onClose: () => void; notchFilters: FilterMap; eogFilters: FilterMap; selectedChannels: number[]; }
function AnalysisModal({ recordingData, onClose }: AnalysisModalProps) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 999, background: "rgba(3,7,18,0.92)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#0b1628", border: "1px solid #1e3a5f", borderRadius: 18, width: "min(980px,95vw)", maxHeight: "92vh", overflowY: "auto", padding: 24, scrollbarWidth: "thin", scrollbarColor: "#1e3a5f transparent" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: "#e2e8f0" }}>📊 Recording Analysis</div>
            <div style={{ fontSize: 10, color: "#475569", marginTop: 3 }}>{recordingData.length.toLocaleString()} samples · {SAMPLING_RATE} Hz</div>
          </div>
          <button onClick={onClose} style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 8, color: "#94a3b8", padding: "6px 14px", cursor: "pointer", fontSize: 12 }}>✕ Close</button>
        </div>
        <BandLegendRow />
        <div style={{ textAlign: "center", color: "#334155", padding: 32, fontSize: 13 }}>
          Analysis content — paste your full AnalysisModal body here.
        </div>
      </div>
    </div>
  );
}

// ── REM Activity Badge ────────────────────────────────────────────────────
interface RemBadgeProps {
  remScore: number;
  isDreaming: boolean;
}
function RemBadge({ remScore, isDreaming }: RemBadgeProps) {
  const barColor = isDreaming ? "#c8a050" : remScore > 0.7 ? "#facc15" : "#475569";
  const label    = isDreaming ? "💤 REM · DREAMING" : remScore > 0.7 ? "👁 REM ACTIVE" : "👁 REM WATCH";
  const labelCol = isDreaming ? "#c8a050" : remScore > 0.7 ? "#facc15" : "#475569";

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 5,
      background: isDreaming ? "#1a1000cc" : "#07101ecc",
      border: `1px solid ${isDreaming ? "#c8a05055" : "#1e3a5f"}`,
      borderRadius: 6, padding: "2px 8px",
      animation: isDreaming ? "remPulse 1.8s ease-in-out infinite" : "none",
    }}>
      <span style={{ fontSize: 8, color: labelCol, fontWeight: 700, letterSpacing: 1, whiteSpace: "nowrap" }}>
        {label}
      </span>
      <div style={{ width: 40, height: 4, background: "#1e293b", borderRadius: 3, overflow: "hidden" }}>
        <div style={{
          height: "100%",
          width: `${Math.round(remScore * 100)}%`,
          background: barColor,
          borderRadius: 3,
          transition: "width 1s ease",
        }} />
      </div>
      <span style={{ fontSize: 8, color: barColor, fontWeight: 700, fontVariantNumeric: "tabular-nums", minWidth: 24 }}>
        {Math.round(remScore * 100)}%
      </span>
    </div>
  );
}

// ── Scrolling canvas hook ─────────────────────────────────────────────────
function useScrollingCanvas({
  containerRef, selectedChannels, amplRef,
  connected, paused, timeBase, zoom, scrollSpeed,
}: UseScrollingCanvasOptions): void {
  const state = useRef<ScrollingCanvasState>({
    rings: [], readBuf: [],
    baselineVals: [], smoothedVals: [],
    plotW: 0, rafId: null, canvases: [],
    fracAccum: 0,
    detectedRange: ADC_RANGE_10BIT,
    detectedRangeLocked: false,
    sampleCount: 0, dirty: true,
  });
  const plotWRef = useRef<number>(600);
  const pausedRef      = useRef(paused);
  const speedRef       = useRef(scrollSpeed);
  const zoomRef        = useRef(zoom);
  const selectedRef    = useRef(selectedChannels);
  const timeBaseRef    = useRef(timeBase);

  useEffect(() => { pausedRef.current = paused; }, [paused]);
  useEffect(() => { speedRef.current = scrollSpeed; }, [scrollSpeed]);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  useEffect(() => { selectedRef.current = selectedChannels; }, [selectedChannels]);
  useEffect(() => { timeBaseRef.current = timeBase; }, [timeBase]);

  const buildCanvases = useCallback((): (() => void) | undefined => {
    const container = containerRef.current;
    if (!container) return;

    const S = state.current;
    if (S.rafId) { cancelAnimationFrame(S.rafId); S.rafId = null; }
    while (container.firstChild) container.removeChild(container.firstChild);

    const channels  = selectedRef.current;
    const totalCh   = channels.length;
    const cW        = Math.max(1, container.clientWidth  || 800);
    const cH        = Math.max(1, container.clientHeight || 400);
    const chH       = Math.max(1, Math.floor(cH / totalCh));
    const plotW     = Math.max(1, cW - Y_AXIS_W - 4);
    plotWRef.current = plotW;
    S.rings        = [];
    S.readBuf      = [];
    S.baselineVals = [];
    S.smoothedVals = [];
    S.canvases     = [];
    S.fracAccum    = 0;
    S.dirty        = true;
    S.detectedRangeLocked = false;
    S.detectedRange = ADC_RANGE_10BIT;

    channels.forEach((chNum, idx) => {
      const ch = CHANNELS.find((c) => c.id === chNum);
      if (!ch) return;

      const ring = new RingBuffer(plotW);
      S.rings.push(ring);
      S.readBuf.push(new Float32Array(plotW));

      S.baselineVals.push(null);
      S.smoothedVals.push(null);

      const isLastCh = idx === totalCh - 1;

      const wrapper = document.createElement("div");
      wrapper.style.cssText = "position:relative;flex:1 1 0%;min-height:0;border-bottom:1px solid #0f1f33;display:flex;";

      const canvas  = document.createElement("canvas");
      canvas.width  = cW;
      canvas.height = chH;
      canvas.style.cssText = "display:block;width:100%;height:100%;flex:1;";

      S.canvases.push({ canvas, ch, isLastCh, idx, plotW });

      const badge = document.createElement("div");
      badge.style.cssText = `position:absolute;top:6px;left:${Y_AXIS_W + 6}px;z-index:4;font-size:10px;color:${ch.color};font-weight:700;letter-spacing:1px;pointer-events:none;background:#060d1aaa;padding:1px 6px;border-radius:4px;border:1px solid ${ch.color}44;`;
      badge.innerText = ch.label;
      wrapper.appendChild(badge);
      wrapper.appendChild(canvas);
      container.appendChild(wrapper);
    });

    function drawFrame() {
      S.rafId = requestAnimationFrame(drawFrame);

      let pixelsThisTick = 0;
      if (!pausedRef.current) {
        const liveSpeed = Math.max(MIN_SCROLL_SPEED, Math.min(MAX_SCROLL_SPEED,
          plotWRef.current / (timeBaseRef.current * 60)
        ));
        S.fracAccum += liveSpeed;
        pixelsThisTick = Math.floor(S.fracAccum);
        S.fracAccum -= pixelsThisTick;
      }

      if (pixelsThisTick > 0) {
        S.dirty = true;

        for (let p = 0; p < pixelsThisTick; p++) {
          S.rings.forEach((ring, ci) => {
            const chNum = selectedRef.current[ci];
            const rawVal = amplRef.current[`ch${chNum}`] ?? 0;

            if (!S.detectedRangeLocked && rawVal !== 0) {
              S.detectedRange = detectAdcRange(rawVal);
              S.detectedRangeLocked = true;
            }

            if (S.baselineVals[ci] === null) {
              if (rawVal !== 0) S.baselineVals[ci] = rawVal;
              else return;
            } else {
              S.baselineVals[ci] = (S.baselineVals[ci] as number) * (1 - BASELINE_ALPHA * 5) + rawVal * (BASELINE_ALPHA * 5);
            }

            const acVal = rawVal - (S.baselineVals[ci] as number);
            ring.push(acVal);
            if (ring.filled > S.sampleCount) S.sampleCount = ring.filled;
          });
        }
      }

      if (!S.dirty) return;
      S.dirty = false;

      S.canvases.forEach(({ canvas, ch, isLastCh, idx, plotW: pW }) => {
        const ctx = canvas.getContext("2d");
        const ring = S.rings[idx];
        const readBuf = S.readBuf[idx];
        if (!ctx || !ring || !readBuf) return;

        const W = canvas.width, H = canvas.height;
        const plotX  = Y_AXIS_W;
        const plotW2 = pW ?? W - Y_AXIS_W - 4;
        const plotTop = 4;
        const plotH   = H - plotTop - (isLastCh ? X_AXIS_H : 4);
        const midY    = plotTop + plotH / 2;

        const halfVisible = (S.detectedRange / 4) / zoomRef.current;
        const scale   = ((plotH / 2) * 0.85) / halfVisible;

        ctx.clearRect(0, 0, W, H);

        const gridCols = 10, gridRows = 4;
        ctx.lineWidth = 0.5;
        for (let g = 0; g <= gridCols; g++) {
          const x = plotX + (g / gridCols) * plotW2;
          ctx.strokeStyle = g === 0 || g === gridCols ? "#1e3a5f" : g % 5 === 0 ? "#172840" : "#0e1e30";
          ctx.globalAlpha = g === 0 || g === gridCols ? 0.5 : g % 5 === 0 ? 0.35 : 0.15;
          ctx.beginPath(); ctx.moveTo(x, plotTop); ctx.lineTo(x, plotTop + plotH); ctx.stroke();
        }
        for (let g = 0; g <= gridRows; g++) {
          const y = plotTop + (g / gridRows) * plotH;
          ctx.strokeStyle = g === 0 || g === gridRows ? "#1e3a5f" : "#0e1e30";
          ctx.globalAlpha = g === 0 || g === gridRows ? 0.5 : 0.2;
          ctx.beginPath(); ctx.moveTo(plotX, y); ctx.lineTo(plotX + plotW2, y); ctx.stroke();
        }
        ctx.globalAlpha = 1;

        ctx.strokeStyle = "#38bdf8"; ctx.lineWidth = 1; ctx.setLineDash([3, 5]); ctx.globalAlpha = 0.35;
        ctx.beginPath(); ctx.moveTo(plotX, midY); ctx.lineTo(plotX + plotW2, midY); ctx.stroke();
        ctx.setLineDash([]); ctx.globalAlpha = 1;

        drawBandLines(ctx, { midY, scale, xStart: plotX, xEnd: plotX + plotW2, plotTop, plotH, showLabels: true });

        ctx.font = "8px 'JetBrains Mono',monospace"; ctx.textAlign = "right"; ctx.textBaseline = "middle";
        for (let g = 0; g <= gridRows; g++) {
          const y = plotTop + (g / gridRows) * plotH;
          const adcVal = Math.round(halfVisible - (g / gridRows) * 2 * halfVisible);
          ctx.strokeStyle = "#1e3a5f"; ctx.lineWidth = 0.5; ctx.globalAlpha = 0.6;
          ctx.beginPath(); ctx.moveTo(plotX - 4, y); ctx.lineTo(plotX, y); ctx.stroke();
          ctx.globalAlpha = 1;
          ctx.fillStyle = adcVal === 0 ? "#4a7a9b" : "#2a4055";
          ctx.fillText(adcVal === 0 ? "0" : adcVal > 0 ? `+${adcVal}` : `${adcVal}`, plotX - 6, y);
        }

        ctx.save();
        ctx.translate(8, plotTop + plotH / 2); ctx.rotate(-Math.PI / 2);
        ctx.fillStyle = "#2d4a63"; ctx.font = "8px 'JetBrains Mono',monospace";
        ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText("ΔADCac", 0, 0);
        ctx.restore();

        const baseline = S.baselineVals[idx];
        if (baseline !== null) {
          ctx.save();
          ctx.font = "7px 'JetBrains Mono',monospace";
          ctx.fillStyle = "#2a4a6a";
          ctx.textAlign = "left"; ctx.textBaseline = "top";
          ctx.fillText(`dc≈${Math.round(baseline)}`, plotX + 4, plotTop + 2);
          ctx.restore();
        }

        ctx.strokeStyle = "#1e3a5f"; ctx.lineWidth = 1; ctx.globalAlpha = 0.7;
        ctx.beginPath(); ctx.moveTo(plotX, plotTop); ctx.lineTo(plotX, plotTop + plotH); ctx.stroke();
        ctx.globalAlpha = 1;

        if (isLastCh) {
          const tb = timeBaseRef.current || DEFAULT_TIME_BASE;
          ctx.font = "8px 'JetBrains Mono',monospace"; ctx.textBaseline = "top";
          const ticks = 4;
          for (let t = 0; t <= ticks; t++) {
            const tSec = -(t / ticks) * tb;
            const x = plotX + (t / ticks) * plotW2;
            const lbl = tSec === 0 ? "now" : `${tSec.toFixed(1)}s`;
            ctx.textAlign = t === 0 ? "left" : t === ticks ? "right" : "center";
            ctx.fillStyle = t === 0 ? "#38bdf866" : "#2a4055";
            ctx.fillText(lbl, x, plotTop + plotH + 3);
          }
          ctx.strokeStyle = "#1e3a5f"; ctx.lineWidth = 1; ctx.globalAlpha = 0.7;
          ctx.beginPath(); ctx.moveTo(plotX, plotTop + plotH); ctx.lineTo(plotX + plotW2, plotTop + plotH); ctx.stroke();
          ctx.globalAlpha = 1;
        }

        ring.readInto(readBuf);
        const drawLen = Math.min(ring.filled, readBuf.length);

       ctx.strokeStyle = ch.color; ctx.lineWidth = 1.5; ctx.globalAlpha = 0.92;
ctx.beginPath();
const xScale = plotW2 / (readBuf.length - 1 || 1);

if (drawLen > 0) {
  const clamp = (v: number) => Math.max(plotTop, Math.min(plotTop + plotH, v));
  const px = (k: number) => plotX + k * xScale;
  const py = (k: number) => clamp(midY - readBuf[k] * scale);

  ctx.moveTo(px(0), py(0));

  for (let k = 1; k < drawLen - 1; k++) {
    // Control point = current point; destination = midpoint to next
    const midX = (px(k) + px(k + 1)) / 2;
    const midY2 = (py(k) + py(k + 1)) / 2;
    ctx.quadraticCurveTo(px(k), py(k), midX, midY2);
  }

  // Draw the last segment to the final point
  if (drawLen > 1) {
    ctx.lineTo(px(drawLen - 1), py(drawLen - 1));
  }
}

ctx.stroke(); ctx.globalAlpha = 1;

        const cursorX = plotX;
        ctx.strokeStyle = ch.color; ctx.lineWidth = 1.5; ctx.globalAlpha = 0.45; ctx.setLineDash([2, 4]);
        ctx.beginPath(); ctx.moveTo(cursorX, plotTop); ctx.lineTo(cursorX, plotTop + plotH); ctx.stroke();
        ctx.setLineDash([]); ctx.globalAlpha = 1;

        const currentAC = readBuf[0];
        const dotY = Math.max(plotTop + 4, Math.min(plotTop + plotH - 4, midY - currentAC * scale));
        ([[ 8, "22"], [5, "55"], [3.5, ""]] as [number, string][]).forEach(([r, alpha]) => {
          ctx.beginPath(); ctx.arc(cursorX, dotY, r as number, 0, Math.PI * 2);
          ctx.fillStyle = ch.color + alpha; ctx.globalAlpha = r === 3.5 ? 0.95 : 1; ctx.fill();
        });
        ctx.globalAlpha = 1;

        const fadeW = Math.max(30, Math.floor(plotW2 * 0.12));
        const rightX = plotX + plotW2;
        const fadeGrad = ctx.createLinearGradient(rightX - fadeW, 0, rightX, 0);
        fadeGrad.addColorStop(0, "transparent"); fadeGrad.addColorStop(1, "#07101e");
        ctx.fillStyle = fadeGrad; ctx.fillRect(rightX - fadeW, plotTop, fadeW + 2, plotH);
      });
    }

    S.rafId = requestAnimationFrame(drawFrame);

    return () => {
      if (S.rafId) { cancelAnimationFrame(S.rafId); S.rafId = null; }
    };
  }, [containerRef, amplRef]);

  useEffect(() => {
    const cleanup = buildCanvases();
    return cleanup;
  }, [buildCanvases, timeBase, selectedChannels, connected]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const h = () => { clearTimeout(timer); timer = setTimeout(() => buildCanvases(), 120); };
    window.addEventListener("resize", h);
    return () => { window.removeEventListener("resize", h); clearTimeout(timer); };
  }, [buildCanvases]);

  useEffect(() => {
    if (!connected) {
      const S = state.current;
      S.rings.forEach((r) => r.clear());
      S.readBuf.forEach((b) => b.fill(0));
      S.smoothedVals = S.smoothedVals.map(() => null);
      S.baselineVals = S.baselineVals.map(() => null);
      S.sampleCount  = 0;
      S.dirty        = true;
      S.detectedRangeLocked = false;
      S.detectedRange = ADC_RANGE_10BIT;
    }
  }, [connected]);
}

function useIsMobile(bp = 900): boolean {
  const [is, setIs] = useState(() => typeof window !== "undefined" && window.innerWidth <= bp);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${bp}px)`);
    const h = (e: MediaQueryListEvent) => setIs(e.matches);
    mq.addEventListener("change", h);
    setIs(mq.matches);
    return () => mq.removeEventListener("change", h);
  }, [bp]);
  return is;
}

// ═══════════════════════════════════════════════════════════════════════════
// ── MAIN HzDashboard
// ═══════════════════════════════════════════════════════════════════════════
interface HzDashboardProps {
  eogHistory?: HistoryEntry[];
  currentAmplitude?: AmplitudeMap;
  hzHistory?: HistoryEntry[];
  currentHz?: number;
  connected?: boolean;
  batteryLevel?: number | null;
  onRecord?: () => void;
  isRecording?: boolean;
  recordingElapsedMs?: number;
  recordingData?: RecordingEntry[];
  externalShowAnalysis?: boolean;
  onExternalAnalysisClose?: () => void;
  isPlaying?: boolean;
  isPaused?: boolean;
  onPlayPause?: () => void;
  onStop?: () => void;
  onOpenPractice?: () => void;
  eyeState?: "open" | "half" | "closed" | "dreaming";
  remThreshold?: number;
  onSessionReportChange?: (open: boolean) => void;
}

export default function HzDashboard({
  currentAmplitude = { ch1: 0, ch2: 0, ch3: 0 },
  currentHz = 0,
  connected = false,
  batteryLevel = null,
  onRecord,
  isRecording = false,
  recordingElapsedMs = 0,
  recordingData = [],
  externalShowAnalysis = false,
  onExternalAnalysisClose,
  isPlaying = false,
  isPaused = false,
  onPlayPause,
  onStop,
  onOpenPractice,
  eyeState = "open",
  eogHistory = [],
  remThreshold = 200,
   onSessionReportChange, 
}: HzDashboardProps) {
  const [zoom, setZoom] = useState<number>(1);
  const [timeBase, setTimeBase] = useState<number>(DEFAULT_TIME_BASE);
  const [selectedChannels] = useState<number[]>(DEFAULT_SELECTED_CHANNELS);
  const [paused, setPaused] = useState<boolean>(false);
  const [sessionDuration, setSessionDuration] = useState<number>(0);
  const [eogFilters, setEogFilters] = useState<FilterMap>({ 1: 2, 2: 2, 3: 2 });
  const [notchFilters, setNotchFilters] = useState<FilterMap>({ 1: 1, 2: 1, 3: 1 });
  const [showAnalysis, setShowAnalysis] = useState<boolean>(false);
  const [toolbarExpanded, setToolbarExpanded] = useState(false);

  // ── Internal recording state ──────────────────────────────────────────
  // We manage our own internal rec state so the Record button works without
  // depending on parent prop toggling. If the parent passes isRecording, we
  // use that as the source of truth; otherwise we drive it internally.
  const [internalRecording, setInternalRecording] = useState<boolean>(false);
  const [recPaused, setRecPaused] = useState<boolean>(false);
  const [snapshots, setSnapshots] = useState<SessionSnapshot[]>([]);
  const [showSessionReport, setShowSessionReport] = useState<boolean>(false);
const setSessionReportOpen = (open: boolean) => {
  setShowSessionReport(open);
  onSessionReportChange?.(open);
};
  const [recStartMs, setRecStartMs] = useState<number>(0);
  const [recElapsedMs, setRecElapsedMs] = useState<number>(0);
  const [sessionReportDurationMs, setSessionReportDurationMs] = useState<number>(0);
  const [sessionReportStartedAt, setSessionReportStartedAt] = useState<number>(0);
  const [finalSnapshots, setFinalSnapshots] = useState<SessionSnapshot[]>([]);

  // Refs for snapshot timer (avoids stale closure)
  const snapshotTimerRef  = useRef<ReturnType<typeof setInterval> | null>(null);
  const recElapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const snapshotsRef      = useRef<SessionSnapshot[]>([]);
  const recStartMsRef     = useRef<number>(0);
const [mounted, setMounted] = useState(false);
useEffect(() => setMounted(true), []);

// Lock page scroll while the report is open
useEffect(() => {
  if (!showSessionReport) return;
  const prevOverflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";
  return () => { document.body.style.overflow = prevOverflow; };
}, [showSessionReport]);
  // Keep snapshotsRef in sync
  useEffect(() => { snapshotsRef.current = snapshots; }, [snapshots]);

  // ── Live REM score ────────────────────────────────────────────────────
  const [remScore, setRemScore] = useState<number>(0);

  const scrollSpeed = useMemo(() => deriveScrollSpeed(timeBase, 600), [timeBase]);

  const isMobile    = useIsMobile();
  const hasRecording = recordingData?.length > 0;
  const canvasContainerRef = useRef<HTMLDivElement | null>(null);

  const amplRef = useRef<AmplitudeMap>(currentAmplitude);
  useEffect(() => { amplRef.current = currentAmplitude; }, [currentAmplitude]);

  // Keep eyeState accessible in snapshot timer without re-subscribing
  const eyeStateRef = useRef<string>(eyeState);
  useEffect(() => { eyeStateRef.current = eyeState; }, [eyeState]);

  // Keep ch1Display accessible in snapshot timer
  const ch1DisplayRef = useRef({ raw: 0, ac: 0, baseline: 0 });

  const eogHistoryRef = useRef<HistoryEntry[]>(eogHistory);
  useEffect(() => { eogHistoryRef.current = eogHistory; }, [eogHistory]);

  // ── Live REM score (every 2s, display only) ────────────────────────────
  useEffect(() => {
    if (!connected) { setRemScore(0); return; }
    const id = setInterval(() => {
      setRemScore(computeRemScore(eogHistoryRef.current, remThreshold));
    }, 2000);
    return () => clearInterval(id);
  }, [connected, remThreshold]);

  useEffect(() => { if (externalShowAnalysis) setShowAnalysis(true); }, [externalShowAnalysis]);
  const handleCloseAnalysis = () => { setShowAnalysis(false); onExternalAnalysisClose?.(); };

  const setCh1Notch = (v: number | null) => setNotchFilters((p) => ({ ...p, 1: v }));

  useScrollingCanvas({
    containerRef: canvasContainerRef,
    selectedChannels,
    amplRef,
    connected,
    paused,
    timeBase,
    zoom,
    scrollSpeed,
  });

  const sessionStartRef = useRef<number | null>(null);
  const timerRef        = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!connected) {
      sessionStartRef.current = null;
      setSessionDuration(0);
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    if (!sessionStartRef.current) sessionStartRef.current = Date.now();
    timerRef.current = setInterval(() => setSessionDuration(Math.floor((Date.now() - (sessionStartRef.current as number)) / 1000)), 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [connected, remThreshold]);

  // ── CH1 display ──────────────────────────────────────────────────────
  const [ch1Display, setCh1Display] = useState({ raw: 0, ac: 0, baseline: 0, barPct: 0, dir: classifyDir(0, ADC_RANGE_10BIT), detectedRange: ADC_RANGE_10BIT });
  const ch1BaselineRef = useRef<number | null>(null);
  const ch1DetectedRangeRef = useRef<number>(ADC_RANGE_10BIT);
  const ch1RangeLocked = useRef<boolean>(false);

  useEffect(() => {
    if (!connected) {
      ch1BaselineRef.current = null;
      ch1RangeLocked.current = false;
      ch1DetectedRangeRef.current = ADC_RANGE_10BIT;
      setCh1Display({ raw: 0, ac: 0, baseline: 0, barPct: 0, dir: classifyDir(0, ADC_RANGE_10BIT), detectedRange: ADC_RANGE_10BIT });
      return;
    }

    const interval = setInterval(() => {
      const raw = amplRef.current.ch1 ?? 0;
      if (raw === 0) return;

      if (!ch1RangeLocked.current) {
        ch1DetectedRangeRef.current = detectAdcRange(raw);
        ch1RangeLocked.current = true;
      }

      if (ch1BaselineRef.current === null) {
        ch1BaselineRef.current = raw;
      } else {
        ch1BaselineRef.current = ch1BaselineRef.current * (1 - BASELINE_ALPHA * 5) + raw * (BASELINE_ALPHA * 5);
      }

      const baseline = ch1BaselineRef.current;
      const ac = raw - baseline;
      const range = ch1DetectedRangeRef.current;
      const barPct = Math.min(Math.abs(ac) / (range / 4), 1) * 100;

      // Keep ref in sync for snapshot timer
      ch1DisplayRef.current = { raw, ac, baseline };

      setCh1Display({ raw, ac, baseline, barPct, dir: classifyDir(ac, range), detectedRange: range });
    }, 100);

    return () => clearInterval(interval);
  }, [connected, remThreshold]);

  const { raw: ch1Raw, ac: ch1AC, baseline: ch1Baseline, barPct: ch1BarPct, dir: ch1Dir, detectedRange } = ch1Display;

  // ── Snapshot capture function ─────────────────────────────────────────
  const captureSnapshot = useCallback(() => {
    const { raw, ac, baseline } = ch1DisplayRef.current;
    const now = Date.now();
    const elapsed = now - recStartMsRef.current;

    // Collect last 60 AC values from eogHistory for the mini sparkline
    // (they're stored in the eogHistory ring, newest last)
    const recent = eogHistoryRef.current.slice(-60).map(e => {
      const r = e.ch1 ?? 0;
      // Quick inline baseline subtract using ch1BaselineRef
      return ch1BaselineRef.current !== null ? r - ch1BaselineRef.current : r;
    });

    const acAbs = Math.abs(ac);
    const bandInfo = getBandFromAC(acAbs);

    const snap: SessionSnapshot = {
      timestampMs:  now,
      elapsedMs:    elapsed,
      ch1AC:        ac,
      ch1Raw:       raw,
      ch1Baseline:  baseline,
      eyeState:     eyeStateRef.current,
      bandLabel:    bandInfo.label,
      bandColor:    bandInfo.color,
      acHistory:    recent,
    };

    setSnapshots(prev => [...prev, snap]);
  }, []);

  // ── Internal record start / stop ──────────────────────────────────────
  const startInternalRecording = useCallback(() => {
    const now = Date.now();
    setInternalRecording(true);
    setRecPaused(false);
    setSnapshots([]);
    snapshotsRef.current = [];
    setRecStartMs(now);
    recStartMsRef.current = now;
    setRecElapsedMs(0);

    // Elapsed timer (1s tick)
    if (recElapsedTimerRef.current) clearInterval(recElapsedTimerRef.current);
    recElapsedTimerRef.current = setInterval(() => {
      setRecElapsedMs(Date.now() - recStartMsRef.current);
    }, 1000);

    // Snapshot every 5 minutes — lightweight: just reads existing refs, no new computation
    if (snapshotTimerRef.current) clearInterval(snapshotTimerRef.current);
    snapshotTimerRef.current = setInterval(() => {
      captureSnapshot();
    }, SNAPSHOT_INTERVAL_MS);
  }, [captureSnapshot]);

  const stopInternalRecording = useCallback(() => {
    if (snapshotTimerRef.current)  { clearInterval(snapshotTimerRef.current);   snapshotTimerRef.current  = null; }
    if (recElapsedTimerRef.current) { clearInterval(recElapsedTimerRef.current); recElapsedTimerRef.current = null; }

    // Capture a final snapshot at stop time if at least 30s has passed since the last one
    const lastSnap = snapshotsRef.current[snapshotsRef.current.length - 1];
    const elapsed  = Date.now() - recStartMsRef.current;
    if (!lastSnap || (Date.now() - lastSnap.timestampMs) > 30_000) {
      // Inline capture since setSnapshots may not have flushed
      const { raw, ac, baseline } = ch1DisplayRef.current;
      const recent = eogHistoryRef.current.slice(-60).map(e => {
        const r = e.ch1 ?? 0;
        return ch1BaselineRef.current !== null ? r - ch1BaselineRef.current : r;
      });
      const bandInfo = getBandFromAC(Math.abs(ac));
      const finalSnap: SessionSnapshot = {
        timestampMs: Date.now(), elapsedMs: elapsed,
        ch1AC: ac, ch1Raw: raw, ch1Baseline: baseline,
        eyeState: eyeStateRef.current,
        bandLabel: bandInfo.label, bandColor: bandInfo.color,
        acHistory: recent,
      };
      const allSnaps = [...snapshotsRef.current, finalSnap];
      setFinalSnapshots(allSnaps);
    } else {
      setFinalSnapshots([...snapshotsRef.current]);
    }

    setSessionReportDurationMs(elapsed);
    setSessionReportStartedAt(recStartMsRef.current);
    setInternalRecording(false);
    setRecPaused(false);
    setRecElapsedMs(elapsed);

    // Show report after a short delay so state settles
    setTimeout(() => setSessionReportOpen(true), 150);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (snapshotTimerRef.current)   clearInterval(snapshotTimerRef.current);
      if (recElapsedTimerRef.current) clearInterval(recElapsedTimerRef.current);
    };
  }, []);

  // ── Record button handler ─────────────────────────────────────────────
  const handleRecordClick = () => {
    if (!internalRecording) {
      startInternalRecording();
      onRecord?.();                        // notify parent (optional)
    } else {
      stopInternalRecording();
      onRecord?.();                        // notify parent (optional)
    }
  };

  const handleRecPause = () => {
    setRecPaused(true);
    if (snapshotTimerRef.current)   { clearInterval(snapshotTimerRef.current);   snapshotTimerRef.current  = null; }
    if (recElapsedTimerRef.current) { clearInterval(recElapsedTimerRef.current); recElapsedTimerRef.current = null; }
  };

  const handleRecResume = () => {
    setRecPaused(false);
    // Restart elapsed timer (keep elapsed offset)
    const offset = recElapsedMs;
    const resumeAt = Date.now() - offset;
    recStartMsRef.current = resumeAt;

    if (recElapsedTimerRef.current) clearInterval(recElapsedTimerRef.current);
    recElapsedTimerRef.current = setInterval(() => {
      setRecElapsedMs(Date.now() - recStartMsRef.current);
    }, 1000);

    if (snapshotTimerRef.current) clearInterval(snapshotTimerRef.current);
    snapshotTimerRef.current = setInterval(() => {
      captureSnapshot();
    }, SNAPSHOT_INTERVAL_MS);
  };

  const activeBand  = getActiveBand(currentHz);
  const statusColor = !connected ? "#64748b" : (activeBand?.color ?? "#34d399");
  const statusLabel = !connected ? "Disconnected" : activeBand ? `${activeBand.label} — ${activeBand.desc}` : "Connected";

  const isDreaming  = eyeState === "dreaming";
  const showRemBadge = connected && (eyeState === "closed" || isDreaming);

  useEffect(() => {
    const s = document.createElement("style");
    s.textContent = `
      input[type=range].hz-slider::-webkit-slider-thumb{-webkit-appearance:none;width:14px;height:14px;border-radius:50%;background:#38bdf8;cursor:pointer;border:2px solid #060d1a;box-shadow:0 0 6px #38bdf844;}
      .hz-panel-btn:hover{background:#1e293b !important;}
      .hz-legend::-webkit-scrollbar{display:none;}
      .hz-body-scroll{scrollbar-width:thin;scrollbar-color:#1e293b transparent;}
      .hz-body-scroll::-webkit-scrollbar{width:4px;}
      .hz-body-scroll::-webkit-scrollbar-track{background:transparent;}
      .hz-body-scroll::-webkit-scrollbar-thumb{background:#1e293b;border-radius:2px;}
      @keyframes battPulse{0%,100%{box-shadow:0 0 0 0 transparent;border-color:#f8717188;}50%{box-shadow:0 0 8px 2px #f8717133;border-color:#f87171cc;}}
      @keyframes battCellPulse{0%,100%{opacity:.9;}50%{opacity:.4;}}
      @keyframes slideDown{from{opacity:0;transform:translateY(-6px);}to{opacity:1;transform:translateY(0);}}
      @keyframes recpulse{0%,100%{opacity:1;}50%{opacity:.25;}}
      @keyframes remPulse{0%,100%{box-shadow:0 0 0 0 transparent;border-color:#c8a05055;}50%{box-shadow:0 0 10px 2px #c8a05033;border-color:#c8a050aa;}}
      @keyframes dreamingGlow{0%,100%{text-shadow:0 0 8px #c8a05066;}50%{text-shadow:0 0 18px #c8a050cc;}}
    `;
    document.head.appendChild(s);
    return () => { document.head.removeChild(s); };
  }, []);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", background: "#060d1a", color: "#e2e8f0", fontFamily: FONT_MONO, overflow: "hidden" }}>
      {showAnalysis && hasRecording && (
        <AnalysisModal recordingData={recordingData} notchFilters={notchFilters} eogFilters={eogFilters}
          selectedChannels={[1]} onClose={handleCloseAnalysis} />
      )}

  {showSessionReport && mounted && createPortal(
  <SessionReportModal
    snapshots={finalSnapshots}
    totalDurationMs={sessionReportDurationMs}
    startedAt={sessionReportStartedAt}
    onClose={() => setSessionReportOpen(false)}
  />,
  document.body
)}

      {/* ── TOOLBAR ── */}
      <div style={{ background: "#0b1628", borderBottom: "1px solid #1e293b", flexShrink: 0, position: "relative", zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", padding: "4px 10px", gap: 8 }}>
          <div style={{ flex: 1 }} />

          {connected && sessionDuration > 0 && (
            <div style={{ background: "#0f172a", border: "1px solid #1e3a5f", borderRadius: 8, padding: "2px 8px", color: "#7aa4c4", fontSize: 11, whiteSpace: "nowrap", flexShrink: 0 }}>
              ⏱ {fmtElapsed(sessionDuration)}
            </div>
          )}

          {/* Dreaming state badge in toolbar */}
          {isDreaming && (
            <div style={{
              display: "flex", alignItems: "center", gap: 5,
              background: "#1a1000", border: "1px solid #c8a05088",
              borderRadius: 8, padding: "2px 10px",
              animation: "remPulse 1.8s ease-in-out infinite",
              flexShrink: 0,
            }}>
              <span style={{ fontSize: 12 }}>💤</span>
              <span style={{
                fontSize: 10, fontWeight: 800, color: "#c8a050",
                letterSpacing: 1,
                animation: "dreamingGlow 2s ease-in-out infinite",
              }}>
                REM · DREAMING
              </span>
            </div>
          )}

          {/* Internal recording elapsed badge in toolbar */}
          {internalRecording && (
            <div style={{ background: "#1e0b0b", border: "1px solid #f8717144", borderRadius: 8, padding: "2px 8px", color: "#f87171", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: recPaused ? "#facc15" : "#f87171", display: "inline-block", animation: recPaused ? "none" : "recpulse 1s infinite" }} />
              {recPaused ? "⏸" : "⏺"} {fmtTime(recElapsedMs)}
              {snapshots.length > 0 && (
                <span style={{ fontSize: 9, color: "#f8717188", marginLeft: 3 }}>
                  {snapshots.length}×📸
                </span>
              )}
            </div>
          )}

          {/* Legacy isRecording badge (from parent prop) */}
          {isRecording && !internalRecording && (
            <div style={{ background: "#1e0b0b", border: "1px solid #f8717144", borderRadius: 8, padding: "2px 8px", color: "#f87171", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#f87171", display: "inline-block", animation: "recpulse 1s infinite" }} />
              {fmtTime(recordingElapsedMs)}
            </div>
          )}

          {isMobile && (
            <button onClick={() => setToolbarExpanded((v) => !v)}
              style={{ padding: "4px 9px", borderRadius: 8, fontSize: 12, fontWeight: 700, border: "1px solid #334155", background: toolbarExpanded ? "#38bdf822" : "transparent", color: toolbarExpanded ? "#38bdf8" : "#64748b", cursor: "pointer", flexShrink: 0 }}>
              {toolbarExpanded ? "▲" : "▼"}
            </button>
          )}
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="hz-body-scroll" style={{ flex: 1, overflowY: "hidden", overflowX: "hidden", minHeight: 0 }}>
        <div style={{ display: "flex", flexDirection: "column", padding: isMobile ? "5px 8px 8px" : "6px 12px 8px", gap: 5, height: "100%", minHeight: "fit-content" }}>

          {/* ── 2-COLUMN ROW ── */}
          <div style={{ display: "flex", gap: isMobile ? 5 : 7, flexShrink: 0, alignItems: "stretch" }}>

            {/* LEFT: CH1 card */}
            <div style={{
              flex: 1, minWidth: 0,
              background: isDreaming ? "#1a100d" : "#38bdf80d",
              border: `1px solid ${isDreaming ? "#c8a05055" : "#38bdf855"}`,
              borderRadius: 12, padding: isMobile ? "5px 8px" : "7px 10px",
              display: "flex", flexDirection: "column", gap: 4,
              transition: "background 1s, border-color 1s",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 10, color: isDreaming ? "#c8a050" : "#38bdf8", fontWeight: 700, letterSpacing: 1 }}>
                  {isDreaming ? "💤 CH1 · REM" : "CH1"}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 5, background: "#0f172a", border: `1px solid ${statusColor}33`, borderRadius: 20, padding: "2px 8px", flexShrink: 0 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: statusColor, boxShadow: `0 0 6px ${statusColor}`, display: "inline-block", flexShrink: 0 }} />
                  <span style={{ color: statusColor, fontSize: 9, fontWeight: 700, whiteSpace: "nowrap" }}>
                    {isMobile ? (connected ? "ON" : "OFF") : statusLabel}
                  </span>
                </div>
              </div>

              <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color: isDreaming ? "#c8a050" : "#38bdf8", lineHeight: 1, transition: "color 1s" }}>
                {(ch1AC > 0 ? "+" : "") + ch1AC.toFixed(0)}
                <span style={{ fontSize: 9, color: "#475569", fontWeight: 400, marginLeft: 3 }}>Δac</span>
              </div>

              <div style={{ height: 3, background: "#1e293b", borderRadius: 4, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", left: "50%", top: 0, width: 1, height: "100%", background: "#334155" }} />
                <div style={{ position: "absolute", left: ch1AC >= 0 ? "50%" : `${50 - ch1BarPct / 2}%`, width: `${ch1BarPct / 2}%`, top: 0, height: "100%", background: isDreaming ? "#c8a050" : ch1AC >= 0 ? "#38bdf8" : "#a78bfa", borderRadius: 4, transition: "width 0.1s, left 0.1s" }} />
              </div>

              <div style={{ fontSize: 9, color: "#334155" }}>
                raw: {ch1Raw.toFixed(0)} · dc≈{ch1Baseline.toFixed(0)}
              </div>

              <div style={{ display: "flex", gap: 4 }}>
                {([
                  ["PEAK+", Math.abs(ch1AC) > 0 ? +ch1AC.toFixed(0) : 0, isDreaming ? "#c8a050" : "#38bdf8"],
                  ["DC",    +ch1Baseline.toFixed(0),                      "#facc15"],
                  ["ADC",   detectedRange === ADC_RANGE_12BIT ? 12 : 10,  "#a78bfa"],
                ] as [string, number, string][]).map(([lbl, val, clr]) => (
                  <div key={lbl} style={{ flex: 1, textAlign: "center", background: "#0a1525", border: `1px solid ${clr}22`, borderRadius: 6, padding: "3px 2px" }}>
                    <div style={{ fontSize: 7, color: "#475569", letterSpacing: 1 }}>{lbl}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: clr }}>
                      {lbl === "ADC" ? `${val}b` : (val > 0 && lbl === "PEAK+" ? "+" : "") + val}
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <span style={{ fontSize: 8, color: "#334155", letterSpacing: 1 }}>NOTCH</span>
                    <NotchBtnGroup active={notchFilters[1] as number | null} onSelect={setCh1Notch} />
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: Panel */}
            <div style={{ flex: 1, minWidth: 0, background: "#0a1525", border: "1px solid #1e293b", borderRadius: 12, padding: isMobile ? "6px 8px" : "7px 10px", display: "flex", flexDirection: "column", gap: 7, overflow: "hidden" }}>

              <div>
                <div style={{ fontSize: 9, color: "#475569", textTransform: "uppercase", letterSpacing: 2, fontWeight: 700, marginBottom: 5, display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ color: "#facc15" }}>⚡</span> Device &amp; Controls
                </div>

                <div style={{ marginBottom: 5 }}>
                  <BatteryIndicator level={batteryLevel} connected={connected} />
                </div>

                {/* ── Recording controls ── */}
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                  <button
                    onClick={() => setPaused((p) => !p)}
                    disabled={!connected}
                    style={{ flex: "1 1 auto", padding: "5px 8px", borderRadius: 8, fontSize: 11, fontWeight: 700, border: "1px solid #334155", background: paused ? "#1e3a5f" : "transparent", color: paused ? "#38bdf8" : "#64748b", cursor: connected ? "pointer" : "not-allowed", opacity: connected ? 1 : 0.4, whiteSpace: "nowrap" }}>
                    {paused ? "▶ Resume" : "⏸ Pause"}
                  </button>

                  {/* ── Record / Pause-rec / Stop-rec button group ── */}
                  {!internalRecording ? (
                    // Not recording: show enabled Record button
                    <button
                      onClick={handleRecordClick}
                      disabled={!connected}
                      style={{
                        flex: "1 1 auto", padding: "5px 8px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                        border: "1px solid #34d39955",
                        background: "#34d39911",
                        color: connected ? "#34d399" : "#334155",
                        cursor: connected ? "pointer" : "not-allowed",
                        opacity: connected ? 1 : 0.4,
                        whiteSpace: "nowrap",
                      }}>
                      ⏺ Record
                    </button>
                  ) : (
                    // Recording: show Pause + Stop buttons in place of Record
                    <>
                      <button
                        onClick={recPaused ? handleRecResume : handleRecPause}
                        style={{
                          flex: "1 1 auto", padding: "5px 8px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                          border: `1px solid ${recPaused ? "#facc1555" : "#facc1533"}`,
                          background: recPaused ? "#facc1522" : "#facc1511",
                          color: "#facc15",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}>
                        {recPaused ? "▶ Resume" : "⏸ Pause"}
                      </button>
                      <button
                        onClick={stopInternalRecording}
                        style={{
                          flex: "1 1 auto", padding: "5px 8px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                          border: "1px solid #f8717144",
                          background: "#f8717122",
                          color: "#f87171",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}>
                        ⏹ Stop
                      </button>
                    </>
                  )}

                  {/* Show session report button if we have saved snapshots */}
                  {!internalRecording && finalSnapshots.length > 0 && (
                    <button
                    onClick={() => setSessionReportOpen(true)}
                      style={{ flex: "1 1 auto", padding: "5px 8px", borderRadius: 8, fontSize: 11, fontWeight: 700, border: "1px solid #a78bfa44", background: "#a78bfa11", color: "#a78bfa", cursor: "pointer", whiteSpace: "nowrap" }}>
                      🌙 Report
                    </button>
                  )}

                  {/* Legacy analysis button */}
                  {!isRecording && hasRecording && !internalRecording && finalSnapshots.length === 0 && (
                    <button
                      onClick={() => setShowAnalysis(true)}
                      style={{ flex: "1 1 auto", padding: "5px 8px", borderRadius: 8, fontSize: 11, fontWeight: 700, border: "1px solid #a78bfa44", background: "#a78bfa11", color: "#a78bfa", cursor: "pointer", whiteSpace: "nowrap" }}>
                      📊 Analysis
                    </button>
                  )}
                </div>

                {/* Live snapshot count while recording */}
                {internalRecording && (
                  <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 5, padding: "3px 7px", background: "#0a1525", border: "1px solid #1e293b", borderRadius: 6 }}>
                    <span style={{ fontSize: 8, color: "#334155" }}>📸 Snapshots:</span>
                    <span style={{ fontSize: 9, color: "#a78bfa", fontWeight: 700 }}>{snapshots.length}</span>
                    <span style={{ fontSize: 8, color: "#1e3a5f" }}>· next in ~{5 - Math.floor((recElapsedMs % SNAPSHOT_INTERVAL_MS) / 60000)}m</span>
                  </div>
                )}
              </div>

              <div style={{ height: 1, background: "#1e293b" }} />

              <div>
                <div style={{ fontSize: 9, color: "#475569", textTransform: "uppercase", letterSpacing: 2, fontWeight: 700, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ color: "#38bdf8" }}>⚙</span> Settings
                  <span style={{ fontSize: 8, color: "#1e3a5f", background: "#07101e", border: "1px solid #1e3a5f", borderRadius: 4, padding: "1px 6px", marginLeft: 4 }}>
                    scroll {scrollSpeed.toFixed(1)}px/f
                  </span>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <LabelSlider label="Zoom" value={zoom} min={1} max={10} step={0.5} onChange={setZoom}
                    fmtVal={(v) => `${v}x`} color="#38bdf8" />
                  <LabelSlider label="Time" value={timeBase} min={MIN_TIME_BASE} max={MAX_TIME_BASE} onChange={setTimeBase}
                    fmtVal={(v) => `${v}s`} color="#a78bfa" />
                </div>
              </div>

              {!isMobile && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: "auto" }}>
                  {[
                    ["ADC", detectedRange === ADC_RANGE_12BIT ? "12-bit" : "10-bit", "#38bdf8"],
                    ["DC", "slow EMA", "#facc15"],
                    ["GRAPH", "AC deflection", "#34d399"],
                  ].map(([k, v, c]) => (
                    <div key={k as string} style={{ display: "flex", gap: 4, alignItems: "center", background: "#07101e", border: `1px solid ${c as string}22`, borderRadius: 5, padding: "2px 6px" }}>
                      <span style={{ fontSize: 8, color: "#334155", fontWeight: 700 }}>{k as string}</span>
                      <span style={{ fontSize: 8, color: c as string }}>{v as string}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Canvas waveform area ── */}
          <div style={{
            background: isDreaming ? "#0a0d07" : "#07101e",
            border: `1px solid ${isDreaming ? "#c8a05033" : "#1e293b"}`,
            borderRadius: 14, position: "relative", overflow: "hidden",
            flex: "3 1 0", display: "flex", flexDirection: "column",
            transition: "background 1s, border-color 1s",
          }}>
            <div style={{ display: "flex", alignItems: "center", padding: isMobile ? "3px 8px" : "4px 12px", flexShrink: 0, position: "relative", zIndex: 5, background: "linear-gradient(to bottom,#07101eee 70%,transparent)", flexWrap: "wrap", gap: 4 }}>
              <span style={{ fontSize: 8, color: "#334155", textTransform: "uppercase", letterSpacing: 2 }}>
                {SAMPLING_RATE} Hz · {timeBase}s{paused ? " · ⏸" : " · live"}
              </span>
              {!isMobile && (
                <>
                  <span style={{ fontSize: 9, color: "#38bdf8", background: "#38bdf811", borderRadius: 4, padding: "1px 6px" }}>↑ RIGHT (+AC)</span>
                  <span style={{ fontSize: 9, color: "#a78bfa", background: "#a78bfa11", borderRadius: 4, padding: "1px 6px" }}>↓ LEFT (−AC)</span>
                  <span style={{ fontSize: 9, color: "#facc15", background: "#facc1511", borderRadius: 4, padding: "1px 6px" }}>
                    ADC: {detectedRange === ADC_RANGE_12BIT ? "12-bit" : "10-bit"} · DC baseline subtracted
                  </span>
                </>
              )}
              {showRemBadge && (
                <div style={{ marginLeft: "auto" }}>
                  <RemBadge remScore={remScore} isDreaming={isDreaming} />
                </div>
              )}
            </div>

            {!connected && (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, position: "absolute", inset: 0, zIndex: 2, background: "#07101ecc" }}>
                <span style={{ fontSize: 30 }}>👁</span>
                <span style={{ fontSize: 12, color: "#334155", textAlign: "center", padding: "0 20px" }}>Connect Dream Sensor to see live waveform</span>
              </div>
            )}

            {connected && sessionDuration > 0 && sessionDuration < 10 && (
              <div style={{ position: "absolute", bottom: 30, left: "50%", transform: "translateX(-50%)", zIndex: 3, background: "#facc1522", border: "1px solid #facc1544", borderRadius: 8, padding: "4px 14px", fontSize: 10, color: "#facc15", whiteSpace: "nowrap", pointerEvents: "none" }}>
                ⏳ Baseline converging… ({10 - sessionDuration}s)
              </div>
            )}

            <div ref={canvasContainerRef} style={{ flex: 1, position: "relative", display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }} />
          </div>

          {/* ── Sleep state labels ── */}
          <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", background: "#07101e", borderRadius: 10, border: "1px solid #1e293b", flexShrink: 0, overflowX: "auto" }}>
            <span style={{ fontSize: 8, color: "#334155", fontWeight: 700, textTransform: "uppercase", letterSpacing: 2, whiteSpace: "nowrap" }}>State</span>
            <div style={{ width: 1, height: 12, background: "#1e293b" }} />
            {([
              ["Awake",      "open",     "#4fc3f7"],
              ["Drowsy",     "half",     "#9b72dd"],
              ["Deep sleep", "closed",   "#3ecb8a"],
              ["Dreaming",   "dreaming", "#c8a050"],
            ] as [string, string, string][]).map(([label, state, color]) => (
              <div key={label} style={{
                padding: "3px 9px", borderRadius: 20, fontSize: 9, fontWeight: 700,
                whiteSpace: "nowrap", letterSpacing: 0.5,
                background: eyeState === state ? `${color}22` : "transparent",
                border: `1px solid ${eyeState === state ? color + "55" : "#1e293b"}`,
                color: eyeState === state ? color : "#334155",
                transition: "all .3s",
                boxShadow: eyeState === state && state === "dreaming" ? `0 0 10px ${color}44` : "none",
                animation: eyeState === state && state === "dreaming" ? "remPulse 1.8s ease-in-out infinite" : "none",
              }}>
                {label}{state === "dreaming" && eyeState === "dreaming" ? " 💤" : ""}
              </div>
            ))}

            {showRemBadge && (
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                <span style={{ fontSize: 8, color: "#475569" }}>REM</span>
                <div style={{ width: 48, height: 4, background: "#1e293b", borderRadius: 3, overflow: "hidden" }}>
                  <div style={{
                    height: "100%",
                    width: `${Math.round(remScore * 100)}%`,
                    background: remScore >= 1 ? "#c8a050" : remScore > 0.6 ? "#facc15" : "#38bdf8",
                    borderRadius: 3,
                    transition: "width 1s ease, background 1s ease",
                  }} />
                </div>
                <span style={{ fontSize: 8, color: "#64748b", minWidth: 22 }}>{Math.round(remScore * 100)}%</span>
              </div>
            )}
          </div>

          {/* ── Band legend ── */}
          <div className="hz-legend" style={{ display: "flex", gap: 7, padding: "4px 10px", background: "#07101e", borderRadius: 10, border: "1px solid #1e293b", fontSize: 8, color: "#475569", alignItems: "center", flexWrap: "nowrap", overflowX: "auto", overflowY: "hidden", flexShrink: 0, scrollbarWidth: "none" }}>
            {BAND_AMP_LINES.map((b) => (
              <span key={b.label} style={{ display: "flex", alignItems: "center", gap: 3, flexShrink: 0, background: b.color + "12", border: `1px solid ${b.color}33`, borderRadius: 4, padding: "1px 5px", whiteSpace: "nowrap" }}>
                <svg width="14" height="8" style={{ flexShrink: 0 }}>
                  <line x1="0" y1="4" x2="14" y2="4" stroke={b.color} strokeWidth="1.5" strokeDasharray={b.dash.join(",")} opacity="0.9" />
                </svg>
                <span style={{ color: b.color }}>{b.label}</span>
                <span style={{ color: "#1e3a5f" }}>±{b.ampUV}</span>
              </span>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}